<script setup>
import { ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
  'text/csv',
];

const isDragOver = ref(false);
const isUploading = ref(false);
const uploadProgress = ref(0);
const fileInput = ref(null);
const mode = ref('file');

const textName = ref('');
const textContent = ref('');
const isSubmittingText = ref(false);

const pickFile = () => fileInput.value?.click();

const uploadFile = async file => {
  if (!file || !ACCEPTED_TYPES.includes(file.type)) {
    useAlert(t('AI_AGENT.KNOWLEDGE.UPLOAD.INVALID_TYPE'));
    return;
  }

  const params = new FormData();
  params.append('file', file);

  isUploading.value = true;
  uploadProgress.value = 0;
  try {
    await store.dispatch('aiBotDocuments/create', {
      botId: props.bot.id,
      params,
      onUploadProgress: event => {
        if (event.total) {
          uploadProgress.value = Math.round((event.loaded / event.total) * 100);
        }
      },
    });
    useAlert(t('AI_AGENT.KNOWLEDGE.UPLOAD.SUCCESS'));
  } catch (error) {
    useAlert(t('AI_AGENT.KNOWLEDGE.UPLOAD.ERROR'));
  } finally {
    isUploading.value = false;
    uploadProgress.value = 0;
    if (fileInput.value) fileInput.value.value = '';
  }
};

const onFileInputChange = event => {
  uploadFile(event.target.files?.[0]);
};

const onDrop = event => {
  isDragOver.value = false;
  uploadFile(event.dataTransfer?.files?.[0]);
};

const submitText = async () => {
  if (!textName.value.trim() || !textContent.value.trim()) return;

  const params = new FormData();
  params.append('name', textName.value.trim());
  params.append('raw_text', textContent.value);

  isSubmittingText.value = true;
  try {
    await store.dispatch('aiBotDocuments/create', {
      botId: props.bot.id,
      params,
    });
    useAlert(t('AI_AGENT.KNOWLEDGE.UPLOAD.SUCCESS'));
    textName.value = '';
    textContent.value = '';
  } catch (error) {
    useAlert(t('AI_AGENT.KNOWLEDGE.UPLOAD.ERROR'));
  } finally {
    isSubmittingText.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center gap-2">
      <Button
        size="sm"
        :faded="mode !== 'file'"
        :label="$t('AI_AGENT.KNOWLEDGE.UPLOAD.FILE_TAB')"
        @click="mode = 'file'"
      />
      <Button
        size="sm"
        :faded="mode !== 'text'"
        :label="$t('AI_AGENT.KNOWLEDGE.UPLOAD.TEXT_TAB')"
        @click="mode = 'text'"
      />
    </div>

    <template v-if="mode === 'file'">
      <div
        class="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-4 py-8 transition-colors"
        :class="
          isDragOver
            ? 'border-n-brand bg-n-brand-1'
            : 'border-n-weak bg-n-solid-2'
        "
        data-testid="upload-dropzone"
        @dragover.prevent="isDragOver = true"
        @dragleave.prevent="isDragOver = false"
        @drop.prevent="onDrop"
      >
        <p class="text-body-main text-n-slate-11">
          {{ $t('AI_AGENT.KNOWLEDGE.UPLOAD.DROPZONE') }}
        </p>
        <Button
          size="sm"
          faded
          slate
          :label="$t('AI_AGENT.KNOWLEDGE.UPLOAD.BROWSE')"
          :is-loading="isUploading"
          @click="pickFile"
        />
        <p class="text-xs text-n-slate-10">
          {{ $t('AI_AGENT.KNOWLEDGE.UPLOAD.ACCEPTED') }}
        </p>
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          accept=".pdf,.docx,.txt,.md,.csv"
          data-testid="upload-file-input"
          @change="onFileInputChange"
        />
      </div>
      <div
        v-if="isUploading"
        class="w-full h-2 rounded-full bg-n-solid-3 overflow-hidden"
      >
        <div
          class="h-2 rounded-full bg-n-brand transition-all"
          :style="{ width: `${uploadProgress}%` }"
          data-testid="upload-progress"
        />
      </div>
    </template>

    <form
      v-else
      class="flex flex-col gap-3"
      data-testid="upload-text-form"
      @submit.prevent="submitText"
    >
      <Input
        id="kb-text-name"
        v-model="textName"
        :label="$t('AI_AGENT.KNOWLEDGE.UPLOAD.TEXT_NAME')"
        :placeholder="$t('AI_AGENT.KNOWLEDGE.UPLOAD.TEXT_NAME_PLACEHOLDER')"
        data-testid="upload-text-name"
      />
      <TextArea
        id="kb-text-content"
        v-model="textContent"
        :label="$t('AI_AGENT.KNOWLEDGE.UPLOAD.TEXT_CONTENT')"
        :placeholder="$t('AI_AGENT.KNOWLEDGE.UPLOAD.TEXT_CONTENT_PLACEHOLDER')"
        data-testid="upload-text-content"
      />
      <div class="flex justify-end">
        <Button
          type="submit"
          size="sm"
          :label="$t('AI_AGENT.KNOWLEDGE.UPLOAD.SUBMIT_TEXT')"
          :is-loading="isSubmittingText"
          :disabled="!textName.trim() || !textContent.trim()"
          data-testid="upload-text-submit"
        />
      </div>
    </form>
  </div>
</template>
